/**
 * GeoModal — Modal de verificação de disponibilidade regional
 * 
 * Aparece na rota / (sem locale) para usuários sem cookie elon_geo_done.
 * Busca dados geo via /api/v1/geo-data (Cloudflare headers).
 * Mostra animação de verificação → sucesso com bandeira + CTA.
 * Sem redirect automático — usuário escolhe clicar.
 * 100% configurável via admin_settings.
 */

import { useState, useEffect, useRef } from 'preact/hooks';
import { useTranslation } from 'react-i18next';
import { T } from '../styles/tokens';
import { Button, Input, Divider } from './ui';
import { api, ApiError } from '../api/client';

interface GeoData {
  country: string;
  countryCode: string;
  city: string;
  region: string;
  locale: string;
  flag: string;
  enabled: boolean;
  settings: {
    steps: string;
    success_text: string;
    scarcity_text: string;
    scarcity_slots: string;
    redirect_delay: string;
    skip_text: string;
    cta_text: string;
    cookie_days: string;
    transition_delay: string;
    success_delay: string;
    progress_easing: string;
    fade_in_duration: string;
    step_animation: string;
    success_animation: string;
  };
}

interface Step {
  icon: string;
  text: string;
  duration: number;
}

declare global {
  interface Window { google: any; }
}

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? match[2] : null;
}

function setCookie(name: string, value: string, days: number) {
  document.cookie = `${name}=${value}; max-age=${days * 86400}; path=/; SameSite=Lax`;
}

// SVG Icons profissionais (inline, sem emoji)
function IconSearch({ size = 32, color = '#2F6BFF' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function IconGlobe({ size = 32, color = '#7C3AED' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  );
}

function IconBarChart({ size = 32, color = '#06b6d4' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="20" x2="12" y2="10" />
      <line x1="18" y1="20" x2="18" y2="4" />
      <line x1="6" y1="20" x2="6" y2="16" />
    </svg>
  );
}

function IconShield({ size = 32, color = '#22c55e' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

function IconCheck({ size = 32, color = '#22c55e' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

function IconMapPin({ size = 32, color = '#f59e0b' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

function IconZap({ size = 24, color = '#f59e0b' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} stroke={color} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  );
}

function IconArrowRight({ size = 18, color = '#fff' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 6, verticalAlign: 'middle' }}>
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}

// Mapeamento icon string → componente SVG
const ICON_MAP: Record<string, (props: { size?: number }) => any> = {
  'search': IconSearch,
  'globe': IconGlobe,
  'chart': IconBarChart,
  'shield': IconShield,
  'check': IconCheck,
  'map': IconMapPin,
  // Fallback para emojis antigos do admin_settings
  '🔍': IconSearch,
  '🌐': IconGlobe,
  '📊': IconBarChart,
  '🛡️': IconShield,
  '✅': IconCheck,
  '📍': IconMapPin,
};

function StepIcon({ icon, size = 40 }: { icon: string; size?: number }) {
  const Component = ICON_MAP[icon];
  if (Component) return <Component size={size} />;
  // Fallback: se for emoji não mapeado, renderiza como SVG circle com texto
  return (
    <div style={{
      width: size,
      height: size,
      borderRadius: '50%',
      background: 'rgba(47,107,255,0.1)',
      border: '2px solid rgba(47,107,255,0.2)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: size * 0.5,
    }}>
      {icon}
    </div>
  );
}

// Mapeamento animação → nome keyframe
function getStepAnimationName(type: string): string {
  switch (type) {
    case 'fade': return 'geo-fade-in';
    case 'slide-left': return 'geo-slide-left';
    case 'scale': return 'geo-scale-step';
    default: return 'geo-step-in'; // slide-up
  }
}

function getSuccessAnimationName(type: string): string {
  switch (type) {
    case 'fade': return 'geo-fade-in';
    case 'slide-up': return 'geo-step-in';
    case 'confetti': return 'geo-success-confetti';
    default: return 'geo-success-bounce'; // scale-bounce
  }
}

// Keyframes CSS
const keyframes = `
@keyframes geo-fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}
@keyframes geo-scale-in {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}
@keyframes geo-step-in {
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes geo-slide-left {
  from { opacity: 0; transform: translateX(20px); }
  to { opacity: 1; transform: translateX(0); }
}
@keyframes geo-scale-step {
  from { opacity: 0; transform: scale(0.9); }
  to { opacity: 1; transform: scale(1); }
}
@keyframes geo-success-bounce {
  0% { opacity: 0; transform: scale(0.8); }
  60% { opacity: 1; transform: scale(1.05); }
  100% { opacity: 1; transform: scale(1); }
}
@keyframes geo-success-confetti {
  0% { opacity: 0; transform: scale(0.5) rotate(-5deg); }
  50% { opacity: 1; transform: scale(1.08) rotate(1deg); }
  100% { opacity: 1; transform: scale(1) rotate(0); }
}
@keyframes geo-progress {
  from { width: 0%; }
  to { width: 100%; }
}
@keyframes geo-check-in {
  from { opacity: 0; transform: scale(0.5); }
  to { opacity: 1; transform: scale(1); }
}
@keyframes geo-shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}
`;

export function GeoModal() {
  const { t, i18n } = useTranslation(['common', 'auth']);
  const [visible, setVisible] = useState(false);
  const [geoData, setGeoData] = useState<GeoData | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [phase, setPhase] = useState<'loading' | 'steps' | 'success' | 'login' | 'sent'>('loading');
  const [steps, setSteps] = useState<Step[]>([]);
  const [progress, setProgress] = useState(0);
  const styleRef = useRef<HTMLStyleElement | null>(null);
  // Login inline state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [authConfig, setAuthConfig] = useState<Record<string, string> | null>(null);
  const googleBtnRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (getCookie('elon_geo_done')) return;

    const path = window.location.pathname;
    if (path !== '/' && path !== '') return;

    const style = document.createElement('style');
    style.textContent = keyframes;
    document.head.appendChild(style);
    styleRef.current = style;

    fetch('/api/v1/geo-data')
      .then(r => r.json())
      .then((data: GeoData) => {
        if (!data.enabled) return;
        setGeoData(data);
        let parsedSteps: Step[] = [];
        try { parsedSteps = JSON.parse(data.settings.steps); } catch {}
        if (parsedSteps.length === 0) parsedSteps = [
          { icon: 'search', text: 'Verificando disponibilidade...', duration: 1000 },
          { icon: 'globe', text: 'Detectando localização...', duration: 1000 },
          { icon: 'chart', text: 'Analisando cobertura...', duration: 1000 },
        ];
        // Se locale não é pt-BR, sobrescrever steps com traduções i18n
        const lang = i18n.language;
        if (lang && !lang.startsWith('pt')) {
          parsedSteps = parsedSteps.map((s, idx) => ({
            ...s,
            text: t(`geo_modal.default_step_${idx + 1}`, s.text),
          }));
        }
        setSteps(parsedSteps);
        setPhase('steps');
        setVisible(true);
      })
      .catch(() => {});

    return () => {
      if (styleRef.current) styleRef.current.remove();
    };
  }, []);

  // Controlar etapas sequenciais com timing configurável
  useEffect(() => {
    if (phase !== 'steps' || steps.length === 0) return;

    const transDelay = parseInt(geoData?.settings.transition_delay || '600') || 600;
    const succDelay = parseInt(geoData?.settings.success_delay || '800') || 800;

    if (currentStep >= steps.length) {
      // Pausa antes de mostrar sucesso (labor illusion)
      const timer = setTimeout(() => setPhase('success'), succDelay);
      return () => clearTimeout(timer);
    }

    // Calcular progresso incluindo transições
    const totalStepTime = steps.reduce((sum, s) => sum + s.duration, 0);
    const totalTransTime = Math.max(0, steps.length - 1) * transDelay;
    const totalDuration = totalStepTime + totalTransTime;
    const elapsedSteps = steps.slice(0, currentStep).reduce((sum, s) => sum + s.duration, 0);
    const elapsedTrans = currentStep * transDelay;
    setProgress(Math.round(((elapsedSteps + elapsedTrans) / totalDuration) * 100));

    const timer = setTimeout(() => {
      // Após duração da etapa, adicionar pausa de transição
      if (currentStep + 1 >= steps.length) {
        setProgress(100);
        setCurrentStep(currentStep + 1);
      } else {
        // Transição entre etapas
        setTimeout(() => {
          setCurrentStep(currentStep + 1);
        }, transDelay);
      }
    }, steps[currentStep].duration);

    return () => clearTimeout(timer);
  }, [phase, currentStep, steps, geoData]);

  // Carregar auth config quando entrar na fase login
  useEffect(() => {
    if (phase !== 'login') return;
    api.get<any>('/auth/config').then((res: any) => {
      setAuthConfig(res || {});
      // Inicializar Google Sign-In se habilitado
      const cfg = res || {};
      if (cfg.google_enabled === 'true' && cfg.google_client_id) {
        loadGoogleSignIn(cfg.google_client_id);
      }
    }).catch(() => setAuthConfig({}));
  }, [phase]);

  function loadGoogleSignIn(clientId: string) {
    const initGoogle = () => {
      if (!window.google?.accounts) return;
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: handleGoogleCredential,
        auto_select: false,
        cancel_on_tap_outside: true,
      });
      if (googleBtnRef.current) {
        googleBtnRef.current.innerHTML = '';
        window.google.accounts.id.renderButton(googleBtnRef.current, {
          type: 'standard', size: 'large', text: 'continue_with', width: 360, theme: 'filled_black',
        });
      }
    };
    if (!document.querySelector('script[src*="accounts.google.com/gsi/client"]')) {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.onload = () => initGoogle();
      document.head.appendChild(script);
    } else if ((window as any).google?.accounts) {
      initGoogle();
    }
  }

  async function handleGoogleCredential(response: any) {
    setLoginLoading(true);
    setLoginError('');
    try {
      await api.post('/auth/google', { credential: response.credential });
      doneCookie();
      window.location.href = '/chat';
    } catch (err: any) {
      setLoginError(err instanceof ApiError ? err.message : t('auth:error_google_login'));
      setLoginLoading(false);
    }
  }

  async function handleMagicLink(e: Event) {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');
    try {
      const turnstileToken = (document.querySelector('[name="cf-turnstile-response"]') as HTMLInputElement)?.value ?? 'dev-token';
      await api.post('/auth/magic-link', { email: loginEmail, turnstileToken });
      setPhase('sent');
    } catch (err: any) {
      setLoginError(err instanceof ApiError ? err.message : t('auth:error_send_link'));
    } finally {
      setLoginLoading(false);
    }
  }

  async function handlePasswordLogin(e: Event) {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');
    try {
      const turnstileToken = (document.querySelector('[name="cf-turnstile-response"]') as HTMLInputElement)?.value ?? 'dev-token';
      await api.post('/auth/login-password', { email: loginEmail, password: loginPassword, turnstileToken });
      doneCookie();
      window.location.href = '/chat';
    } catch (err: any) {
      setLoginError(err instanceof ApiError ? err.message : t('auth:error_wrong_credentials'));
    } finally {
      setLoginLoading(false);
    }
  }

  function doneCookie() {
    const cookieDays = parseInt(geoData?.settings.cookie_days || '30') || 30;
    setCookie('elon_geo_done', '1', cookieDays);
  }

  function handleCTA() {
    // Em vez de redirecionar, mostrar login inline
    setPhase('login');
  }

  function handleSkip() {
    doneCookie();
    setVisible(false);
  }

  if (!visible || !geoData) return null;

  // Textos de marketing: pt-BR usa textos do admin (D1), outros idiomas usam i18n
  const isPtBR = !i18n.language || i18n.language.startsWith('pt');

  const successText = (isPtBR
    ? (geoData.settings.success_text || 'Disponível em {city}, {country}!')
    : t('geo_modal.default_success_text', { city: geoData.city, country: geoData.country })
  )
    .replace('{city}', geoData.city)
    .replace('{country}', geoData.country)
    .replace('{flag}', geoData.flag)
    .replace(/^[✅🎉🎊]\s*/g, '');

  const scarcityText = (isPtBR
    ? (geoData.settings.scarcity_text || '')
    : t('geo_modal.default_scarcity_text', { slots: geoData.settings.scarcity_slots || '12' })
  )
    .replace('{slots}', geoData.settings.scarcity_slots || '12')
    .replace(/^[🔥⚡]\s*/g, '');

  const ctaText = (isPtBR
    ? (geoData.settings.cta_text || 'Acessar agora')
    : t('geo_modal.default_cta_text')
  ).replace(/\s*→\s*$/, '');

  const skipText = isPtBR
    ? (geoData.settings.skip_text || 'Pular')
    : t('geo_modal.default_skip_text');

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'rgba(0,0,0,0.6)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        animation: `geo-fade-in ${parseInt(geoData?.settings.fade_in_duration || '300')}ms ease-out`,
        padding: 16,
      }}
    >
      {/* Modal */}
      <div
        style={{
          position: 'relative',
          background: 'rgba(15, 20, 35, 0.98)',
          border: '1px solid rgba(47,107,255,0.12)',
          borderRadius: 20,
          maxWidth: 440,
          width: '100%',
          padding: '40px 32px 32px',
          boxShadow: '0 0 80px rgba(47,107,255,0.06), 0 24px 64px rgba(0,0,0,0.5)',
          animation: 'geo-scale-in 0.4s ease-out',
          overflow: 'hidden',
          textAlign: 'center',
        }}
      >
        {/* Fase: Etapas — Minimalista */}
        {phase === 'steps' && currentStep < steps.length && (
          <div key={currentStep} style={{ animation: `${getStepAnimationName(geoData?.settings.step_animation || 'slide-up')} 0.4s ease-out` }}>
            <div style={{ color: '#e2e8f0', fontSize: 16, fontWeight: 500, lineHeight: 1.6, marginBottom: 6 }}>
              {steps[currentStep].text}
            </div>
            <div style={{ color: '#475569', fontSize: 12, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
              {currentStep + 1} / {steps.length}
            </div>
          </div>
        )}

        {/* Fase: Sucesso — Clean, foco na copy */}
        {phase === 'success' && (
          <div style={{ animation: `${getSuccessAnimationName(geoData?.settings.success_animation || 'scale-bounce')} 0.5s ease-out` }}>
            {/* Bandeira grande como hero */}
            <div style={{ fontSize: 48, marginBottom: 16, lineHeight: 1 }}>{geoData.flag}</div>

            {/* Headline principal */}
            <div style={{
              color: '#fff',
              fontSize: 20,
              fontWeight: 700,
              marginBottom: 8,
              lineHeight: 1.3,
              letterSpacing: '-0.02em',
            }}>
              {successText}
            </div>

            {/* Escassez — texto puro, sem pill/badge/ícone */}
            {scarcityText && (
              <div style={{
                color: '#94a3b8',
                fontSize: 14,
                marginBottom: 28,
                lineHeight: 1.5,
              }}>
                {scarcityText}
              </div>
            )}

            {/* CTA Button — limpo */}
            <button
              onClick={handleCTA}
              style={{
                display: 'block',
                width: '100%',
                padding: '14px 24px',
                background: 'linear-gradient(135deg, #2F6BFF, #7C3AED)',
                color: '#fff',
                border: 'none',
                borderRadius: 12,
                fontSize: 15,
                fontWeight: 600,
                cursor: 'pointer',
                marginBottom: 12,
                transition: 'transform 0.15s, box-shadow 0.15s',
                boxShadow: '0 4px 20px rgba(47,107,255,0.25)',
                letterSpacing: '-0.01em',
              }}
              onMouseOver={(e: any) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 6px 28px rgba(47,107,255,0.35)';
              }}
              onMouseOut={(e: any) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 20px rgba(47,107,255,0.25)';
              }}
            >
              {ctaText}
            </button>

            {/* Skip — discreto */}
            <button
              onClick={handleSkip}
              style={{
                background: 'none',
                border: 'none',
                color: '#475569',
                fontSize: 13,
                cursor: 'pointer',
                padding: '8px 16px',
                transition: 'color 0.15s',
              }}
              onMouseOver={(e: any) => { e.currentTarget.style.color = '#94a3b8'; }}
              onMouseOut={(e: any) => { e.currentTarget.style.color = '#475569'; }}
            >
              {skipText}
            </button>
          </div>
        )}

        {/* Fase: Login inline — idêntico ao Login.tsx */}
        {phase === 'login' && authConfig && (() => {
          const googleEnabled = authConfig.google_enabled === 'true' && !!authConfig.google_client_id;
          const magicLinkEnabled = authConfig.magic_link_enabled !== 'false';
          const passwordEnabled = authConfig.password_login_enabled !== 'false';
          const turnstileSiteKey = authConfig.turnstile_site_key || '1x00000000000000000000AA';
          return (
          <div style={{ animation: 'geo-step-in 0.3s ease-out', textAlign: 'left' }}>
            {/* Header — mesmo estilo Login.tsx */}
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <h2 style={{
                fontSize: T.text2xl, fontWeight: T.weightBold, color: T.text,
                margin: '0 0 6px', letterSpacing: '-0.5px',
              }}>
                {t('auth:login_title')}
              </h2>
              <p style={{ fontSize: T.textMd, color: T.textSecondary, margin: 0 }}>
                {t('auth:login_subtitle')}
              </p>
            </div>

            {/* Conteúdo do login — SEM card extra (o modal externo já é o card) */}
            <div>
              {loginError && (
                <div style={{
                  background: T.dangerBg, border: `1px solid ${T.dangerBgStrong}`,
                  borderRadius: T.radiusMd, padding: '10px 14px', marginBottom: 16,
                  fontSize: T.textSm, color: T.danger,
                }}>
                  {loginError}
                </div>
              )}

              {/* Google Sign-In — wrapper com visual overlay + GIS real transparente */}
              {googleEnabled && (
                <>
                  <div data-google-btn style={{ position: 'relative', width: '100%', height: 44, marginBottom: 4 }}>
                    {/* Nosso visual (não-interativo) */}
                    <div style={{
                      position: 'absolute', inset: 0, borderRadius: T.radiusMd,
                      border: `1px solid ${T.border}`, background: T.bgCard,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                      pointerEvents: 'none', zIndex: 1,
                    }}>
                      <svg width="18" height="18" viewBox="0 0 24 24">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A11.96 11.96 0 001 12c0 1.94.46 3.77 1.18 5.43l3.66-2.84z" fill="#FBBC05"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                      </svg>
                      <span style={{ fontSize: T.textMd, fontWeight: T.weightSemibold, color: T.text }}>{t('auth:continue_with_google')}</span>
                    </div>
                    {/* Botão GIS real (transparente — recebe clique real) */}
                    <div ref={googleBtnRef} style={{
                      position: 'absolute', inset: 0, zIndex: 2, opacity: 0.01, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }} />
                  </div>
                  {(magicLinkEnabled || passwordEnabled) && <Divider label="ou" spacing={16} />}
                </>
              )}

              {/* Formulário magic link */}
              {magicLinkEnabled && !showPasswordForm && (
                <form onSubmit={handleMagicLink}>
                  <Input
                    label={t('auth:email_label')}
                    type="email"
                    placeholder={t('auth:email_placeholder')}
                    value={loginEmail}
                    onChange={(v) => setLoginEmail(v)}
                  />
                  <div class="cf-turnstile" data-sitekey={turnstileSiteKey} data-theme="dark" style={{ marginTop: 12 }} />
                  <Button
                    variant="primary"
                    size="lg"
                    type="submit"
                    disabled={loginLoading || !loginEmail}
                    style={{ width: '100%', marginTop: 16, justifyContent: 'center' }}
                  >
                    {loginLoading ? t('auth:sending') : (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="white" stroke="none">
                          <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                        </svg>
                        {t('auth:enter_magic_link')}
                      </>
                    )}
                  </Button>
                </form>
              )}

              {/* Divisor + login com senha */}
              {passwordEnabled && magicLinkEnabled && !showPasswordForm && <Divider label="ou" spacing={20} />}

              {/* Toggle: entrar com senha */}
              {passwordEnabled && !showPasswordForm ? (
                <Button variant="ghost"
                  onClick={() => { setShowPasswordForm(true); setLoginError(''); }}
                  style={{
                    width: '100%', padding: '10px 0', background: 'none',
                    border: 'none', color: T.textMuted, fontSize: T.textSm,
                    cursor: 'pointer', transition: `color ${T.transitionFast}`,
                    fontWeight: T.weightMedium,
                  }}
                >
                  {t('auth:enter_with_password')}
                </Button>
              ) : passwordEnabled && showPasswordForm ? (
                <form onSubmit={handlePasswordLogin}>
                  <Input
                    label={t('auth:email_label')}
                    type="email"
                    placeholder={t('auth:email_placeholder')}
                    value={loginEmail}
                    onChange={(v) => setLoginEmail(v)}
                  />
                  <div style={{ marginTop: 10 }}>
                    <Input
                      label={t('auth:password_label')}
                      type="password"
                      placeholder={t('auth:password_placeholder')}
                      value={loginPassword}
                      onChange={(v) => setLoginPassword(v)}
                    />
                  </div>
                  <div class="cf-turnstile" data-sitekey={turnstileSiteKey} data-theme="dark" style={{ marginTop: 12 }} />
                  <Button
                    variant="primary"
                    size="lg"
                    type="submit"
                    disabled={loginLoading || !loginEmail || !loginPassword}
                    style={{ width: '100%', marginTop: 16, justifyContent: 'center' }}
                  >
                    {loginLoading ? t('auth:entering') : (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                        </svg>
                        {t('auth:enter')}
                      </>
                    )}
                  </Button>
                  <Button variant="ghost" type="button" onClick={() => { setShowPasswordForm(false); setLoginError(''); }} style={{ width: '100%', marginTop: 8 }}>
                    {t('auth:back_to_magic_link')}
                  </Button>
                </form>
              ) : null}
            </div>

            {/* Footer: Criar conta + Continuar sem conta */}
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <p style={{ fontSize: T.textSm, color: T.textMuted, margin: '0 0 8px' }}>
                {t('common:no_account')}{' '}
                <a href="/signup" style={{ color: T.primary, textDecoration: 'none', fontWeight: T.weightSemibold }}>
                  {t('common:create_free_account')}
                </a>
              </p>
              <button
                onClick={handleSkip}
                style={{
                  background: 'none', border: 'none', color: T.textMuted,
                  fontSize: T.textXs, cursor: 'pointer', padding: '6px 16px',
                  transition: `color ${T.transitionFast}`,
                }}
                onMouseOver={(e: any) => { e.currentTarget.style.color = T.textSecondary; }}
                onMouseOut={(e: any) => { e.currentTarget.style.color = T.textMuted; }}
              >
                {t('common:continue_without_account')}
              </button>
            </div>
          </div>
          );
        })()}

        {/* Fase: Magic link enviado — idêntico ao SentState do Login.tsx */}
        {phase === 'sent' && (
          <div style={{ animation: 'geo-step-in 0.3s ease-out', textAlign: 'center' }}>
            {/* Ícone avião */}
            <div style={{
              width: 64, height: 64, borderRadius: '50%', margin: '0 auto 24px',
              background: T.primaryBg, border: `1px solid ${T.primaryBgStrong}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={T.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 2L11 13" /><path d="M22 2L15 22L11 13L2 9L22 2Z" />
              </svg>
            </div>

            <h2 style={{ fontSize: T.text2xl, fontWeight: T.weightBold, color: T.text, margin: '0 0 8px' }}>
              {t('auth:email_sent')}
            </h2>
            <p style={{ color: T.textSecondary, fontSize: T.textMd, marginBottom: 20, lineHeight: 1.5 }}>
              {t('auth:magic_link_sent')}
            </p>

            {/* Badge do email */}
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: T.primaryBg, border: `1px solid ${T.primaryBgStrong}`,
              borderRadius: T.radiusMd, padding: '10px 20px', marginBottom: 24,
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="4" width="20" height="16" rx="2" /><path d="M22 4L12 13L2 4" />
              </svg>
              <span style={{ color: T.primary, fontSize: T.textMd, fontWeight: T.weightSemibold, wordBreak: 'break-all' }}>
                {loginEmail}
              </span>
            </div>

            {/* Info cards */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
              <div style={{
                flex: 1, background: T.bgCard, borderRadius: T.radiusMd,
                padding: '14px 12px', border: `1px solid ${T.border}`,
              }}>
                <div style={{ fontSize: T.textXs, color: T.textMuted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: T.weightSemibold }}>{t('auth:expires_in')}</div>
                <div style={{ fontSize: T.textXl, fontWeight: T.weightBold, color: T.text }}>{t('auth:minutes')}</div>
              </div>
              <div style={{
                flex: 1, background: T.bgCard, borderRadius: T.radiusMd,
                padding: '14px 12px', border: `1px solid ${T.border}`,
              }}>
                <div style={{ fontSize: T.textXs, color: T.textMuted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: T.weightSemibold }}>{t('auth:not_received')}</div>
                <div style={{ fontSize: T.textSm, fontWeight: T.weightMedium, color: T.textSecondary }}>{t('auth:check_spam')}</div>
              </div>
            </div>

            <Button variant="ghost" onClick={() => { setPhase('login'); setLoginError(''); }}>
              {t('auth:try_another_email')}
            </Button>
          </div>
        )}

        {/* Fase: Carregando auth config */}
        {phase === 'login' && !authConfig && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ color: '#64748b', fontSize: 14 }}>{t('loading')}</div>
          </div>
        )}

        {/* Progress bar */}
        {phase === 'steps' && (
          <div style={{
            width: '100%',
            height: 3,
            background: 'rgba(255,255,255,0.04)',
            borderRadius: 2,
            overflow: 'hidden',
            marginTop: 4,
          }}>
            <div style={{
              height: '100%',
              width: `${progress}%`,
              background: 'linear-gradient(90deg, #2F6BFF, #7C3AED)',
              borderRadius: 2,
              transition: `width 0.4s ${geoData?.settings.progress_easing || 'ease-out'}`,
            }} />
          </div>
        )}

        {/* Skip durante steps */}
        {phase === 'steps' && (
          <button
            onClick={handleSkip}
            style={{
              background: 'none',
              border: 'none',
              color: '#475569',
              fontSize: 12,
              cursor: 'pointer',
              padding: '12px 16px 0',
              transition: 'color 0.15s',
            }}
            onMouseOver={(e: any) => { e.currentTarget.style.color = '#94a3b8'; }}
            onMouseOut={(e: any) => { e.currentTarget.style.color = '#475569'; }}
          >
            {skipText}
          </button>
        )}
      </div>
    </div>
  );
}
